import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert, Stack, IconButton, InputAdornment, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import { useThemeColors } from "../../../hooks/useThemeColors";

interface TokenRevealDialogProps {
  token: string | null;
  onClose: () => void;
  /** Opens the external API documentation; omitted on pages without the docs dialog. */
  onShowDocs?: () => void;
}

const TokenRevealDialog: React.FC<TokenRevealDialogProps> = ({ token, onClose, onShowDocs }) => {
  const headingColor = useThemeColors().primary[100];
  const handleCopy = () => {
    if (!token) return;
    navigator.clipboard.writeText(token);
    toast.success("Token copied to clipboard", { position: "top-right" });
  };

  return (
    <Dialog open={Boolean(token)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: headingColor }}>API Token</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">
            This token will not be shown again. Copy it now and store it securely.
          </Alert>
          <TextField
            value={token || ""}
            fullWidth
            InputProps={{
              readOnly: true,
              sx: { fontFamily: "monospace", fontSize: "0.8125rem" },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopy} edge="end">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Use it as <code>Authorization: Bearer &lt;token&gt;</code> against the external API. The
            index endpoint lists the tables this application may read.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        {onShowDocs && <Button onClick={onShowDocs}>How to use this token</Button>}
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TokenRevealDialog;
